import { createFileRoute } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import { ContentHeader, ContentSection, FeatureShowcase, FAQSection, CTASection } from "@/components/content";

export const Route = createFileRoute('/concepts/ai-agents')({
  component: AIAgentsPage,
  head: () => ({
    meta: [
      { title: 'What are AI Agents? | Matricks' },
      { name: 'description', content: 'Learn what AI agents are, how they work, and how they can automate complex tasks. Complete guide to autonomous AI systems and agent orchestration.' },
    ],
  }),
});

const capabilities = [
  {
    iconName: "Brain",
    title: "Reasoning & Planning",
    description: "AI agents can break down complex tasks into steps, reason about the best approach, and adapt their plans based on new information.",
  },
  {
    iconName: "Zap",
    title: "Tool Usage",
    description: "Agents can use external tools like APIs, databases, and code execution to accomplish tasks beyond pure text generation.",
  },
  {
    iconName: "MessageSquare",
    title: "Natural Language Understanding",
    description: "Communicate with users in natural language, understanding context, intent, and nuance in conversations.",
  },
  {
    iconName: "Workflow",
    title: "Task Automation",
    description: "Automate repetitive tasks by combining multiple actions into coherent workflows executed autonomously.",
  },
  {
    iconName: "Database",
    title: "Memory & Context",
    description: "Maintain context across interactions, remember previous conversations, and learn from past experiences.",
  },
  {
    iconName: "Bot",
    title: "Multi-Agent Collaboration",
    description: "Multiple specialized agents can work together, each handling different aspects of a complex problem.",
  },
];

const useCases = [
  {
    title: "Customer Support",
    description: "AI agents can handle customer inquiries, resolve issues, and escalate complex cases to human agents.",
  },
  {
    title: "Data Analysis",
    description: "Agents can analyze datasets, generate reports, and provide insights without manual data processing.",
  },
  {
    title: "Code Generation",
    description: "Development agents can write, review, and debug code, significantly accelerating development workflows.",
  },
  {
    title: "Research & Summarization",
    description: "Agents can research topics, synthesize information from multiple sources, and produce comprehensive summaries.",
  },
];

const faqItems = [
  {
    question: "How are AI agents different from chatbots?",
    answer: "Traditional chatbots follow predefined scripts and can only respond to specific inputs. AI agents, powered by large language models, can understand context, reason about problems, use tools, and take autonomous actions. They're more flexible and capable of handling novel situations.",
  },
  {
    question: "What LLMs power AI agents?",
    answer: "AI agents can be built on various LLMs including GPT-4, Claude, Gemini, and open-source models like Llama. The choice of model affects the agent's capabilities in areas like reasoning, code generation, and instruction following.",
  },
  {
    question: "Are AI agents safe to use in production?",
    answer: "With proper guardrails, AI agents can be deployed safely in production. Key safety measures include limiting tool access, implementing human-in-the-loop for critical decisions, monitoring agent actions, and setting clear boundaries on what actions agents can take.",
  },
  {
    question: "How do agents use tools?",
    answer: "Agents are provided with tool definitions that describe available capabilities (like API calls or database queries). When the agent determines it needs a tool, it generates a structured request that the system executes, returning results the agent can use to continue its task.",
  },
  {
    question: "What is agent orchestration?",
    answer: "Agent orchestration is the coordination of multiple AI agents working together. This includes routing tasks to appropriate agents, managing agent communication, handling failures, and combining agent outputs into coherent results.",
  },
];

function AIAgentsPage() {
  return (
    <div>
      <ContentHeader
        title="What are AI Agents?"
        description="AI agents are autonomous software systems powered by large language models that can perceive their environment, make decisions, and take actions to accomplish goals with minimal human intervention."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Concepts", href: "/concepts" },
          { label: "AI Agents" },
        ]}
        badge="Concept"
      />

      {/* What are AI Agents */}
      <ContentSection id="what-are-ai-agents" title="Understanding AI Agents">
        <div className="prose prose-gray dark:prose-invert max-w-none">
          <p className="text-lg text-muted-foreground leading-relaxed">
            AI agents represent a significant evolution beyond traditional chatbots and simple AI assistants.
            While a chatbot responds to individual prompts, an AI agent can pursue complex goals across
            multiple steps, using tools and making decisions along the way.
          </p>

          <p className="text-muted-foreground leading-relaxed mt-4">
            At their core, AI agents combine large language models (LLMs) with the ability to take actions
            in the real world. They can call APIs, query databases, send emails, generate code, and interact
            with other systems—all while reasoning about the best way to accomplish their objectives.
          </p>

          <h3 className="text-xl font-semibold mt-8 mb-4">The Agent Loop</h3>
          <div className="rounded-xl border bg-muted/30 p-6 not-prose">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
              <div className="p-4">
                <div className="h-12 w-12 mx-auto rounded-full bg-blue-500/10 flex items-center justify-center mb-2">
                  <span className="text-xl">👁️</span>
                </div>
                <p className="font-medium">Perceive</p>
                <p className="text-xs text-muted-foreground mt-1">Gather input and context</p>
              </div>
              <div className="p-4">
                <div className="h-12 w-12 mx-auto rounded-full bg-purple-500/10 flex items-center justify-center mb-2">
                  <span className="text-xl">🧠</span>
                </div>
                <p className="font-medium">Reason</p>
                <p className="text-xs text-muted-foreground mt-1">Plan and decide</p>
              </div>
              <div className="p-4">
                <div className="h-12 w-12 mx-auto rounded-full bg-green-500/10 flex items-center justify-center mb-2">
                  <span className="text-xl">⚡</span>
                </div>
                <p className="font-medium">Act</p>
                <p className="text-xs text-muted-foreground mt-1">Execute tools and actions</p>
              </div>
              <div className="p-4">
                <div className="h-12 w-12 mx-auto rounded-full bg-orange-500/10 flex items-center justify-center mb-2">
                  <span className="text-xl">🔄</span>
                </div>
                <p className="font-medium">Learn</p>
                <p className="text-xs text-muted-foreground mt-1">Update and iterate</p>
              </div>
            </div>
          </div>
        </div>
      </ContentSection>

      {/* Capabilities */}
      <ContentSection
        id="capabilities"
        title="Key Capabilities"
        description="Modern AI agents possess a range of capabilities that enable autonomous task completion."
        background="muted"
      >
        <FeatureShowcase features={capabilities} columns={3} variant="cards" />
      </ContentSection>

      {/* Use Cases */}
      <ContentSection
        id="use-cases"
        title="Common Use Cases"
        description="AI agents are transforming how businesses operate across many domains."
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {useCases.map((useCase) => (
            <div key={useCase.title} className="rounded-xl border bg-card p-6">
              <h3 className="font-semibold">{useCase.title}</h3>
              <p className="text-sm text-muted-foreground mt-2">{useCase.description}</p>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* Types of Agents */}
      <ContentSection
        id="agent-types"
        title="Types of AI Agents"
        background="muted"
      >
        <div className="space-y-6">
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold text-lg">Single-Purpose Agents</h3>
            <p className="text-muted-foreground mt-2">
              Specialized agents designed for specific tasks like code review, customer support, or data analysis.
              They excel at their designated function and are easier to optimize and monitor.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold text-lg">General-Purpose Agents</h3>
            <p className="text-muted-foreground mt-2">
              Flexible agents capable of handling a wide range of tasks. They use planning and tool selection
              to adapt to different requirements, trading specialization for versatility.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold text-lg">Multi-Agent Systems</h3>
            <p className="text-muted-foreground mt-2">
              Teams of specialized agents that collaborate to solve complex problems. Each agent handles
              its area of expertise, with an orchestrator coordinating their efforts.
            </p>
          </div>
        </div>
      </ContentSection>

      {/* Related Concepts */}
      <ContentSection id="related" title="Related Concepts">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link
            to="/concepts/workflow-automation"
            className="rounded-lg border bg-card p-4 hover:border-primary/50 transition-colors"
          >
            <h4 className="font-medium">Workflow Automation</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Automating business processes with agents
            </p>
          </Link>
          <Link
            to="/features/agents-platform"
            className="rounded-lg border bg-card p-4 hover:border-primary/50 transition-colors"
          >
            <h4 className="font-medium">AI Agents Platform</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Build and deploy agents with Matricks
            </p>
          </Link>
          <Link
            to="/features/ai-gateway"
            className="rounded-lg border bg-card p-4 hover:border-primary/50 transition-colors"
          >
            <h4 className="font-medium">AI/LLM Gateway</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Manage LLM access and costs
            </p>
          </Link>
        </div>
      </ContentSection>

      {/* FAQ */}
      <FAQSection items={faqItems} />

      {/* CTA */}
      <CTASection
        title="Ready to build AI Agents?"
        description="Matricks provides a complete platform for building, deploying, and managing AI agents with enterprise-grade security."
        primaryAction={{ label: "Start Building", href: "/login" }}
        secondaryAction={{ label: "View Agent Platform", href: "/features/agents-platform" }}
        background="primary"
      />
    </div>
  );
}
